import { FormEvent, useEffect, useState } from 'react';
import { apiGet } from '@shared/api/httpClient';
import type { ActivityLogRecord, ActivityResponse } from '@shared/types/api';
import { formatLocalDateTime } from '@shared/utils/dateTime';
import { friendlyLabel, friendlyOptionLabel } from '@shared/utils/friendlyLabels';
import { statusPillClassName } from '@/utils/statusPill';

type SearchMode = 'exact' | 'contains' | 'beginsWith';

type ActivitySearchState = {
  clientId: string;
  eventType: string;
  status: string;
  jobId: string;
  search: string;
  searchMode: SearchMode;
  limit: number;
};

const ACTIVITY_EVENT_TYPE_OPTIONS = [
  'ADMIN_ORCHESTRATION_REQUEST',
  'ACTIVITY',
  'AUDITOR_PACKAGE',
  'CLIENT_REGISTRY',
  'CLIENT_UPLOAD',
  'CMMC_WORKFLOW',
  'JOB_STATUS',
  'PACKAGE_DOWNLOAD',
  'PACKAGE_LIFECYCLE',
  'REPORT_GENERATION',
];

const ACTIVITY_STATUS_OPTIONS = [
  'ACTIVE',
  'ORCHESTRATION_FAILED',
  'ORCHESTRATION_STARTED',
  'REPORT_GENERATION_RUNNING',
  'AUDITOR_PACKAGE_READY',
  'CLIENT_APPROVAL_EMAIL_SENT',
  'CLIENT_APPROVAL_REQUESTED',
  'CLIENT_APPROVED_EVIDENCE_GENERATION',
  'CONTROL_MATRIX_BUILT',
  'DOWNLOAD_LINK_SENT',
  'EVIDENCE_ARCHIVE_REBUILT',
  'EVIDENCE_PACKAGE_UPLOADED',
  'EVIDENCE_VALIDATED',
  'FAILED',
  'PACKAGE_DOWNLOAD_URL_GENERATED',
  'PENDING',
  'PROCESSING',
  'QUEUED',
  'REPORT_GENERATION_SKIPPED',
  'UPLOAD_ACCESS_KEY_CREATED',
  'UPLOAD_URL_GENERATED',
];

const DEFAULT_SEARCH: ActivitySearchState = {
  clientId: '',
  eventType: '',
  status: '',
  jobId: '',
  search: '',
  searchMode: 'contains',
  limit: 25,
};

function value(value: unknown): string {
  if (value === undefined || value === null || value === '') return '—';
  if (Array.isArray(value)) return value.join(', ') || '—';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

function formatDate(dateValue: unknown): string {
  return formatLocalDateTime(dateValue);
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {};
}

function detailRecord(activity: ActivityLogRecord): Record<string, unknown> {
  return asRecord(activity.detail);
}

function field(activity: ActivityLogRecord, ...keys: string[]): unknown {
  const detail = detailRecord(activity);
  for (const key of keys) {
    const directValue = activity[key];
    if (directValue !== undefined && directValue !== null && directValue !== '') return directValue;
    const detailValue = detail[key];
    if (detailValue !== undefined && detailValue !== null && detailValue !== '') return detailValue;
  }
  return undefined;
}

const STATUS_MESSAGES: Record<string, string> = {
  ADMIN_ORCHESTRATION_REQUEST: 'Admin queued evidence workflow for client.',
  ORCHESTRATION_STARTED: 'Centralized CMMC orchestration state machine started.',
  CLIENT_APPROVED_EVIDENCE_GENERATION: 'Client approved evidence generation.',
  CLIENT_DECLINED_EVIDENCE_GENERATION: 'Client declined the workflow request.',
  CLIENT_APPROVAL_REQUESTED: 'Client approval requested before evidence generation.',
  CLIENT_APPROVAL_EMAIL_SENT: 'Client approval email processed.',
  EVIDENCE_VALIDATION_STARTED: 'Evidence validation started.',
  EVIDENCE_VALIDATED: 'Evidence package validation completed.',
  CONTROL_MATRIX_STARTED: 'Control coverage matrix generation started.',
  CONTROL_MATRIX_BUILT: 'Control coverage matrix completed.',
  REPORT_GENERATION_SKIPPED: 'Report generation was skipped for this workflow run.',
  EVIDENCE_ARCHIVE_REBUILT: 'Validated evidence archive was rebuilt.',
  EVIDENCE_PACKAGE_UPLOADED: 'Technical evidence package uploaded.',
  UPLOAD_ACCESS_KEY_CREATED: 'Client upload access key created.',
  PACKAGE_DOWNLOAD_URL_GENERATED: 'Package download URL generated.',
  AUDITOR_PACKAGE_READY: 'Final auditor package is ready.',
  DOWNLOAD_LINK_SENT: 'Final package download link sent.',
};

function friendlyActivityMessage(activity: ActivityLogRecord): string {
  const explicitMessage = value(field(activity, 'message', 'description')).trim();
  if (explicitMessage !== '—') return explicitMessage;

  const status = value(field(activity, 'displayStatus', 'status')).trim();
  if (status && STATUS_MESSAGES[status]) return STATUS_MESSAGES[status];

  const detailStatus = value(field(activity, 'detailStatus')).trim();
  if (detailStatus && STATUS_MESSAGES[detailStatus]) return STATUS_MESSAGES[detailStatus];

  return '—';
}

function displayActivityId(activity: ActivityLogRecord): string {
  const jobId = value(field(activity, 'jobId', 'pipelineExecutionId')).trim();
  const eventId = value(field(activity, 'eventId', 'activityId')).trim();
  if (jobId !== '—') return jobId;
  if (eventId !== '—') return eventId;
  return value(activity.eventTimeEventId);
}

function rawActivityStatus(activity: ActivityLogRecord): string {
  return value(field(activity, 'displayStatus', 'status', 'jobStatus', 'packageLifecycleStatus', 'clientUploadStatus'));
}

function activityStatus(activity: ActivityLogRecord): string {
  return friendlyLabel(rawActivityStatus(activity));
}

function rawEventType(activity: ActivityLogRecord): string {
  return value(field(activity, 'eventType'));
}

function eventTypeLabel(activity: ActivityLogRecord): string {
  return friendlyLabel(rawEventType(activity));
}

function activityTime(activity: ActivityLogRecord): string {
  return value(field(activity, 'eventTime', 'updatedAt', 'createdAt', 'recordedAt'));
}

function activityId(activity: ActivityLogRecord): string {
  return displayActivityId(activity);
}

function rowKey(activity: ActivityLogRecord, index: number): string {
  return `${value(field(activity, 'clientId'))}-${value(field(activity, 'eventId', 'activityId', 'jobId', 'eventTimeEventId'))}-${index}`;
}

function detailRow(label: string, detailValue: unknown) {
  return (
    <div className="detail-row" key={label}>
      <dt>{label}</dt>
      <dd>{value(detailValue)}</dd>
    </div>
  );
}

function ActivityDetails({ activity }: { activity: ActivityLogRecord }) {
  const detail = detailRecord(activity);
  const message = friendlyActivityMessage(activity);

  return (
    <article className="card detail-panel">
      <div className="detail-panel-header">
        <div>
          <h3>Activity Details</h3>
          <p className="muted-text">{value(field(activity, 'clientName', 'clientId'))} · {activityId(activity)}</p>
        </div>
        <span className={statusPillClassName(rawActivityStatus(activity))}>{activityStatus(activity)}</span>
      </div>

      <div className="activity-message-callout">
        <span className="eyebrow">Activity summary</span>
        <strong>{message}</strong>
      </div>

      <div className="detail-grid">
        <section>
          <h4>Summary</h4>
          <dl>
            {detailRow('Client ID', field(activity, 'clientId'))}
            {detailRow('Client Name', field(activity, 'clientName'))}
            {detailRow('Event ID', field(activity, 'eventId', 'activityId'))}
            {detailRow('Workflow / Job ID', field(activity, 'jobId', 'pipelineExecutionId'))}
            <div className="detail-row"><dt>Event Type</dt><dd><span className={statusPillClassName(rawEventType(activity))}>{eventTypeLabel(activity)}</span></dd></div>
            {detailRow('Event Time', formatDate(field(activity, 'eventTime', 'recordedAt')))}
          </dl>
        </section>

        <section>
          <h4>Lifecycle</h4>
          <dl>
            <div className="detail-row"><dt>Display Status</dt><dd><span className={statusPillClassName(field(activity, 'displayStatus', 'status'))}>{friendlyLabel(field(activity, 'displayStatus', 'status'))}</span></dd></div>
            <div className="detail-row"><dt>Job Status</dt><dd><span className={statusPillClassName(field(activity, 'jobStatus'))}>{friendlyLabel(field(activity, 'jobStatus'))}</span></dd></div>
            <div className="detail-row"><dt>Package Lifecycle</dt><dd><span className={statusPillClassName(field(activity, 'packageLifecycleStatus'))}>{friendlyLabel(field(activity, 'packageLifecycleStatus'))}</span></dd></div>
            <div className="detail-row"><dt>Client Upload</dt><dd><span className={statusPillClassName(field(activity, 'clientUploadStatus'))}>{friendlyLabel(field(activity, 'clientUploadStatus'))}</span></dd></div>
            {detailRow('Workflow Type', field(activity, 'workflowType'))}
          </dl>
        </section>

        <section>
          <h4>Workflow Context</h4>
          <dl>
            {detailRow('Package Name', field(activity, 'packageName'))}
            {detailRow('CMMC Level', field(activity, 'cmmcLevel'))}
            {detailRow('Client Account', field(activity, 'clientAccountId'))}
            {detailRow('Client Region', field(activity, 'clientRegion'))}
            {detailRow('CodeBuild Build', field(activity, 'codeBuildBuildId'))}
            {detailRow('Pipeline Name', field(activity, 'pipelineName'))}
          </dl>
        </section>

        <section>
          <h4>Package</h4>
          <dl>
            {detailRow('Evidence Bucket', field(activity, 'evidencePackageBucket'))}
            {detailRow('Source Upload Key', field(activity, 'sourceUploadKey', 'sourceEvidenceKey'))}
            {detailRow('Preserved Key', field(activity, 'preservedKey'))}
            {detailRow('Final Output Prefix', field(activity, 'finalOutputPrefix'))}
            {detailRow('Metadata Source', field(activity, 'metadataSource'))}
            {detailRow('Package Directory', field(activity, 'packageDir'))}
            {detailRow('Uploaded Size', field(activity, 'uploadedSize'))}
          </dl>
        </section>

        <section>
          <h4>Message</h4>
          <dl>
            {detailRow('Message', message)}
            {detailRow('Created', formatDate(field(activity, 'createdAt')))}
            {detailRow('Updated', formatDate(field(activity, 'updatedAt')))}
            {detailRow('Recorded', formatDate(field(activity, 'recordedAt')))}
          </dl>
        </section>

        {Object.keys(detail).length > 0 && (
          <section>
            <h4>Mapped Detail Fields</h4>
            <dl>
              {detailRow('Job Type', detail.jobType)}
              {detailRow('Report Generation', detail.reportGenerationEnabled)}
              {detailRow('Workflow Type', detail.workflowType)}
              {detailRow('Pipeline Execution', detail.pipelineExecutionId)}
              {detailRow('Package Name', detail.packageName)}
              <div className="detail-row"><dt>Status</dt><dd><span className={statusPillClassName(detail.status)}>{friendlyLabel(detail.status)}</span></dd></div>
            </dl>
          </section>
        )}
      </div>

      <details className="raw-details">
        <summary>Raw record</summary>
        <pre>{JSON.stringify(activity, null, 2)}</pre>
      </details>
    </article>
  );
}

export default function ActivityPage() {
  const [activity, setActivity] = useState<ActivityLogRecord[]>([]);
  const [selectedActivity, setSelectedActivity] = useState<ActivityLogRecord | null>(null);
  const [search, setSearch] = useState<ActivitySearchState>(DEFAULT_SEARCH);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [metadata, setMetadata] = useState<{ count?: number; matchedCount?: number; limit?: number; page?: number; pageCount?: number; hasPreviousPage?: boolean; hasNextPage?: boolean } | null>(null);
  const [page, setPage] = useState(1);

  async function loadActivity(criteria: ActivitySearchState = search, pageNumber = page) {
    setLoading(true);
    setError(null);
    try {
      const response = await apiGet<ActivityResponse>('/activity', {
        query: {
          limit: criteria.limit,
          page: pageNumber,
          clientId: criteria.clientId || undefined,
          eventType: criteria.eventType || undefined,
          status: criteria.status || undefined,
          jobId: criteria.jobId || undefined,
          search: criteria.search || undefined,
          searchMode: criteria.searchMode,
        },
      });
      setActivity(response.activity ?? []);
      setSelectedActivity(null);
      setMetadata({ count: response.count, matchedCount: response.matchedCount, limit: response.limit, page: response.page, pageCount: response.pageCount, hasPreviousPage: response.hasPreviousPage, hasNextPage: response.hasNextPage });
      setPage(response.page ?? pageNumber);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to load activity.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadActivity(DEFAULT_SEARCH, 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleSearch(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setPage(1);
    void loadActivity(search, 1);
  }

  function handleClear() {
    setSearch(DEFAULT_SEARCH);
    setPage(1);
    void loadActivity(DEFAULT_SEARCH, 1);
  }

  return (
    <div className="page-stack">
      <div className="page-heading">
        <h2>Activity</h2>
        <p>Search activity and package lifecycle records. Select a row to view the full details below the grid.</p>
      </div>

      <article className="card">
        <h3>Search activity</h3>
        <form className="form-grid jobs-search-grid" onSubmit={handleSearch}>
          <label>
            Client ID or name
            <input value={search.clientId} onChange={(event) => setSearch({ ...search, clientId: event.target.value })} placeholder="surelinc" />
          </label>
          <label>
            Event or Job ID
            <input value={search.jobId} onChange={(event) => setSearch({ ...search, jobId: event.target.value })} placeholder="2026-05-21T01-10-11Z" />
          </label>
          <label>
            Event Type
            <select value={search.eventType} onChange={(event) => setSearch({ ...search, eventType: event.target.value })}>
              <option value="">All event types</option>
              {ACTIVITY_EVENT_TYPE_OPTIONS.map((eventType) => <option key={eventType} value={eventType}>{eventType}</option>)}
            </select>
          </label>
          <label>
            Status
            <select value={search.status} onChange={(event) => setSearch({ ...search, status: event.target.value })}>
              <option value="">All statuses</option>
              {ACTIVITY_STATUS_OPTIONS.map((status) => <option key={status} value={status}>{friendlyOptionLabel(status, 'All statuses')}</option>)}
            </select>
          </label>
          <label>
            Search text
            <input value={search.search} onChange={(event) => setSearch({ ...search, search: event.target.value })} placeholder="package, uploaded, final, lifecycle" />
          </label>
          <label>
            Search mode
            <select value={search.searchMode} onChange={(event) => setSearch({ ...search, searchMode: event.target.value as SearchMode })}>
              <option value="contains">Contains</option>
              <option value="exact">Exact word</option>
              <option value="beginsWith">Begins with</option>
            </select>
          </label>
          <label>
            Limit
            <input type="number" min="1" max="100" value={search.limit} onChange={(event) => setSearch({ ...search, limit: Number(event.target.value) || 25 })} />
          </label>
          <div className="form-actions form-wide button-row">
            <button className="primary-button" type="submit" disabled={loading}>{loading ? 'Searching...' : 'Search'}</button>
            <button className="secondary-button" type="button" onClick={handleClear} disabled={loading}>Clear</button>
          </div>
        </form>
      </article>

      <article className="card">
        <div className="grid-header-row">
          <div>
            <h3>Activity</h3>
          </div>
          {metadata && <span className="result-count">Showing {activity.length} of {metadata.matchedCount ?? activity.length} matched · Page {metadata.page ?? page}{metadata.pageCount ? ` of ${metadata.pageCount}` : ''}</span>}
        </div>

        {loading && <p>Loading activity...</p>}
        {error && <p className="error-text">{error}</p>}
        {!loading && !error && activity.length === 0 && <p>No activity records matched the current filters.</p>}

        {metadata && !loading && !error && (metadata.hasPreviousPage || metadata.hasNextPage) && (
          <div className="pagination-row">
            <button className="secondary-button" type="button" disabled={!metadata.hasPreviousPage || loading} onClick={() => void loadActivity(search, Math.max(1, page - 1))}>Previous</button>
            <span className="muted-text">Page {metadata.page ?? page}{metadata.pageCount ? ` of ${metadata.pageCount}` : ''}</span>
            <button className="secondary-button" type="button" disabled={!metadata.hasNextPage || loading} onClick={() => void loadActivity(search, page + 1)}>Next</button>
          </div>
        )}
        {!loading && !error && activity.length > 0 && (
          <div className="table-wrap">
            <table className="activity-table interactive-table">
              <thead>
                <tr>
                  <th>Client</th>
                  <th>Event Time</th>
                  <th>Event Type</th>
                  <th>Status</th>
                  <th>Job/Event ID</th>
                  <th>Message</th>
                </tr>
              </thead>
              <tbody>
                {activity.map((item, index) => {
                  const selected = selectedActivity === item;
                  return (
                    <tr key={rowKey(item, index)} className={selected ? 'selected-row' : undefined} onClick={() => setSelectedActivity(item)} tabIndex={0} onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') setSelectedActivity(item); }}>
                      <td>{value(item.clientName || item.clientId)}</td>
                      <td>{formatDate(field(item, 'eventTime', 'updatedAt', 'createdAt', 'recordedAt'))}</td>
                      <td><span className={statusPillClassName(rawEventType(item))}>{eventTypeLabel(item)}</span></td>
                      <td><span className={statusPillClassName(rawActivityStatus(item))}>{activityStatus(item)}</span></td>
                      <td className="mono-cell">{activityId(item)}</td>
                      <td>{friendlyActivityMessage(item)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </article>

      {selectedActivity && <ActivityDetails activity={selectedActivity} />}
    </div>
  );
}
